import { isValidDesktopInstanceId } from './desktopInstanceId';
import { type DesktopTarget, type DesktopTargetListResponse } from './desktopTargets';

export const NO_COMPUTER_SELECTION = 'none';

/** `null` = never written. `'none'` = explicit No computer. Else a `target_id`. */
// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents -- `'none'` documents the explicit No computer sentinel
export type DesktopTargetSticky = string | typeof NO_COMPUTER_SELECTION | null;

type NoneResolvedDesktopTargetSelection = { kind: typeof NO_COMPUTER_SELECTION };
type TargetResolvedDesktopTargetSelection = { kind: 'target'; target: DesktopTarget };
type DeadResolvedDesktopTargetSelection = {
  kind: 'dead';
  targetId: string;
  target?: DesktopTarget;
};

export type ResolvedDesktopTargetSelection =
  | NoneResolvedDesktopTargetSelection
  | TargetResolvedDesktopTargetSelection
  | DeadResolvedDesktopTargetSelection;

export const isThisComputerTarget = (
  target: DesktopTarget,
  instanceId: string | null
): boolean => instanceId !== null && target.instance_id === instanceId;

export const resolveDesktopTargetSelection = ({
  sticky,
  roster,
  instanceId,
}: {
  sticky: DesktopTargetSticky;
  roster?: DesktopTargetListResponse;
  instanceId: string | null;
}): ResolvedDesktopTargetSelection => {
  if (sticky === NO_COMPUTER_SELECTION) return { kind: NO_COMPUTER_SELECTION };

  const targets = roster?.targets;

  if (sticky) {
    const found = targets?.find((target) => target.target_id === sticky);
    // An empty or missing roster leaves No computer as the only choice.
    if (!found && !targets?.length) return { kind: NO_COMPUTER_SELECTION };
    if (!found || !found.is_available) {
      return { kind: 'dead', targetId: sticky, target: found };
    }
    return { kind: 'target', target: found };
  }

  if (!instanceId || !isValidDesktopInstanceId(instanceId)) {
    return { kind: NO_COMPUTER_SELECTION };
  }

  const thisComputer = targets?.find(
    (target) => isThisComputerTarget(target, instanceId) && target.is_available
  );

  if (!thisComputer) {
    return { kind: NO_COMPUTER_SELECTION };
  }

  return { kind: 'target', target: thisComputer };
};

export const targetIdForTurn = (
  selection: ResolvedDesktopTargetSelection
): string | undefined =>
  selection.kind === 'target' ? selection.target.target_id : undefined;

export const groupDesktopTargetsForSelector = (
  targets: readonly DesktopTarget[],
  instanceId: string | null
): {
  thisComputer?: DesktopTarget;
  live: DesktopTarget[];
  dead: DesktopTarget[];
} => {
  let thisComputer: DesktopTarget | undefined;
  const live: DesktopTarget[] = [];
  const dead: DesktopTarget[] = [];
  for (const target of targets) {
    if (isThisComputerTarget(target, instanceId)) {
      thisComputer = target;
      continue;
    }
    if (target.is_available) live.push(target);
    else dead.push(target);
  }
  const byDisplayName = (a: DesktopTarget, b: DesktopTarget) =>
    a.display_name.localeCompare(b.display_name);
  live.sort(byDisplayName);
  dead.sort(byDisplayName);
  return { thisComputer, live, dead };
};

export const selectorValueForSelection = (
  selection: ResolvedDesktopTargetSelection
): string => {
  if (selection.kind === 'target') return selection.target.target_id;
  if (selection.kind === 'dead') return selection.targetId;
  return NO_COMPUTER_SELECTION;
};
